/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import docApi from "api";
import { providers, discovery } from "dc-core";
import {
  getTotalPages,
  isFilePasswordProtected,
  isDocumentEncrypted,
  getFileShareType,
  getIsPortfolio,
  getMaxTotalPageIndex,
  getAssetURI
} from "selectors/documentSelectors";
import PermissionDetailsCSS from "documentProperties/PermissionDetails.css";
import { isModifyingContentAllowed, hasSecurityBitSet } from "utils/DocPermissionsUtils";
import { isReadOnlyModeEnabled } from "selectors/appSelectors";
import { loadStatus, bookmarksStatuses } from "constants/enums";
import { getIntl } from "selectors/appSelectors";
import { getOriginalPageDimensions } from "app/AppInitUtility";
import { dialogApi, dialogTypes } from "components/dialogs";
import { showFileCopyDialog } from "organize/utils/FileCopyDialogUtils";
import { getBookmarksStatus } from "selectors/bookmarksSelectors";
import { overlayEventNames, PAGE_PRIORITY } from "constants/constants";
import { overlayEvents } from "overlays/OverlayEvents";
import OrganizePDFFileFormats from "organize/utils/OrganizePDFFileFormats";
import { triggerOpenDocumentInDesktopEvent } from "publicApis/Events/events";
import { failureType } from "dc-preview-utils/src/Constants/enums";
import {
  logUnsupportedFileDialogShown,
  logSharedFileDialogShown,
  logOrganizeErrorData,
  logOrganizeInsertData,
  logOrganizeCommonData
} from "logging";
import { getHasDigSigFromMetadata } from "selectors/digSigSelectors";
import { getThumbnailDataSource } from "selectors/organizeSelectors";
import { pdfEditOperations } from "dc-preview-utils/src/Constants/enums";
import {
  trackOrganizeRotationTriggered,
  trackOrganizeInsertTriggered,
  trackDeleteOperationTriggered,
  trackReorderOperationTriggered
} from "analytics/organizeEvents";
import { featureLifecycleHookController } from "publicApis/controller/featureLifecycleHookController";
import { featureLifecyleHooks } from "constants/enums";
import { getTileCanvasRef, getTileCanvasRefPerPage } from "selectors/pagedViewSelectors";
import { getPageZoomLevel } from "selectors/zoom";
import { pagesInView } from "selectors/pagedViewSelectors";
import CommandManager from "undoRedo/CommandManager";

const FEATURE_TYPE = "InlineOrganize";
const OP_TYPE = {
  ROTATE_PAGES_CCW: "rotatePagesCCW",
  ROTATE_PAGES_CW: "rotatePagesCW",
  DELETE_PAGES: "deletePages",
  INSERT_PAGES: "insertPages",
  REORDER_PAGES: "reorderPages"
};

const triggerAfterOrganizePages = (type, params) => {
  overlayEvents.trigger(overlayEventNames.ORGANIZE_OP_PERFORMED, {
    opType: type,
    params
  });
};

const triggerBeforeOrganizePages = async params => {
  await featureLifecycleHookController.trigger(featureLifecyleHooks.BEFORE_ORGANIZE_PAGES, params);
};

const loadPage = (stores, pageIndex) => {
  stores.documentStore.unloadPage(pageIndex);
  stores.documentStore.loadPage(pageIndex, getPageZoomLevel(stores, pageIndex), pagesInView(stores), PAGE_PRIORITY);
};

const clearTileCanvas = stores => {
  getTileCanvasRef(stores).forEach((value, key) => {
    loadPage(stores, key);
    const editCanvasRef = getTileCanvasRefPerPage(stores, key);
    editCanvasRef.current = null;
  });
  stores.pagedViewState.setIsTileCanvasLoaded(false);
};

export const getSupportedFileTypes = () => {
  const fileTypes = {
    acceptMimeTypes: ["application/pdf"],
    acceptExtensions: ["pdf"]
  };

  const allowedFormats = OrganizePDFFileFormats.file_formats;
  allowedFormats.forEach(allowedFormat => {
    const extension = Object.keys(allowedFormat)[0];
    if (!fileTypes.acceptExtensions.includes(extension)) {
      fileTypes.acceptExtensions.push(extension);
    }
    const mimeType = allowedFormat[extension].mime_type;
    if (!fileTypes.acceptMimeTypes.includes(mimeType)) {
      fileTypes.acceptMimeTypes.push(mimeType);
    }
  });

  fileTypes.acceptDroppedExtensions = fileTypes.acceptExtensions;
  fileTypes.acceptDroppedMimeTypes = fileTypes.acceptMimeTypes;
  return fileTypes;
};

export const savePDF = async optype => {
  await docApi.organizeDidEnd();
  docApi._pdfEdited(optype);
};

const isDocumentInsertable = async fileBuffer => {
  let fileHandle = null;
  let ajs = null;
  try {
    const ajsPromise = providers.acrobatjs();
    // const fileBuffer = await this.getFileBuffer();
    const openPdfParams = {
      buffer: fileBuffer
    };
    ajs = await ajsPromise;
    fileHandle = await ajs.openPDF(openPdfParams);
    const metaData = await ajs.getMetadata(fileHandle);
    if (
      metaData.data.isEncrypted ||
      metaData.data.hasDigitalSignatures ||
      metaData.data.hasSignMetadata ||
      metaData.data.isPortfolio
    )
      return Promise.resolve(false);
    return Promise.resolve(true);
  } catch (e) {
    console.error("Error: ", e);
    return Promise.resolve(false);
  } finally {
    if (fileHandle) {
      ajs.closePDF(fileHandle);
    }
  }
};

export const callDropinMethod = (dropinId, methodName, opts) =>
  discovery.callBootstrapMethod(dropinId, methodName, opts);
export const callExecuteAction = (verbId, opts = {}) => callDropinMethod(verbId, "executeAction", opts);
export const fetchVerbIcon = (verbId, opts = {}) => callDropinMethod(verbId, "getIcon", opts);
export const fetchAriaLabel = (verbId, opts = {}) => callDropinMethod(verbId, "getAriaLabel", opts);
export const fetchExecuteAction = (verbId, opts = {}) => callDropinMethod(verbId, "executeAction", opts);
export const fetchTooltip = (verbId, opts = {}) => callDropinMethod(verbId, "getTooltip", opts);
export const fetchTooltipDelay = verbId => callDropinMethod(verbId, "getTooltipDelay");

const closeConflictingToolsBoardPanels = () =>
  providers["tools-board-provider"]().then(toolsBoardProvider => {
    const currentConfig = toolsBoardProvider.getConfig();
    if (
      currentConfig &&
      (currentConfig.id === "verb-number-pages-viewer" || currentConfig.id === "verb-crop-pages-viewer")
    ) {
      return toolsBoardProvider.reset();
    }
    return Promise.resolve();
  });

const logOrganizeOpsData = async (featureType, opType, info) => {
  const isInEditMode = await getIsInEditMode();
  const data = {
    featureType,
    opType,
    ...info,
    isInEditMode
  };
  logOrganizeCommonData(data);
};

export const handleCCWRotateClick = async (stores, pageIndices) => {
  await handleErrorHandling(stores, "RotatePages");
  try {
    trackOrganizeRotationTriggered(pageIndices.length);
    await logOrganizeOpsData(FEATURE_TYPE, OP_TYPE.ROTATE_PAGES_CCW, {});
    stores.organizeStore.setIsOrganizeOpInProgress(true);
    await closeConflictingToolsBoardPanels();
    pageIndices = pageIndices.sort();
    const params = {
      pageIndices,
      rotation: 270
    };
    await triggerBeforeOrganizePages({ opType: "rotatePages", ...params });
    await docApi.organizeWillBegin({ intent: "rotate-pages", fileUrl: getAssetURI(stores) });
    await docApi.rotatePages(params);
    CommandManager.clearStack();
    const startPageNum = pageIndices[0] + 1;
    const endPageNum = pageIndices[pageIndices.length - 1] + 1;
    await stores.documentStore.reloadPagesAndGeometries(startPageNum, endPageNum, {
      refreshGeometry: true
    });
    await savePDF(pdfEditOperations.ORGANIZE_PAGE_ROTATED);
    stores.organizeStore.setIsOrganizeOpInProgress(false);
    triggerAfterOrganizePages("rotatePages", params);
    if (getBookmarksStatus(stores) !== bookmarksStatuses.NOT_PRESENT) stores.bookmarkStore.getBookmarksFromDocAPI(true);
  } catch (error) {
    stores.organizeStore.setIsOrganizeOpInProgress(false);
    logOrganizeErrorData({
      featureType: FEATURE_TYPE,
      opType: OP_TYPE.ROTATE_PAGES_CCW,
      errorInfo: { pageIndices, error }
    });
    console.error("Error in handleCWRotateClick", error);
  }
};

export const handleCWRotateClick = async (stores, pageIndices) => {
  await handleErrorHandling(stores, "RotatePages");
  try {
    trackOrganizeRotationTriggered(pageIndices.length);
    await logOrganizeOpsData(FEATURE_TYPE, OP_TYPE.ROTATE_PAGES_CW, {});
    stores.organizeStore.setIsOrganizeOpInProgress(true);
    await closeConflictingToolsBoardPanels();
    pageIndices = pageIndices.sort();
    const params = {
      pageIndices,
      rotation: 90
    };
    await triggerBeforeOrganizePages({ opType: "rotatePages", ...params });
    await docApi.organizeWillBegin({ intent: "rotate-pages", fileUrl: getAssetURI(stores) });
    await docApi.rotatePages(params);
    CommandManager.clearStack();
    const startPageNum = pageIndices[0] + 1;
    const endPageNum = pageIndices[pageIndices.length - 1] + 1;
    await stores.documentStore.reloadPagesAndGeometries(startPageNum, endPageNum, {
      refreshGeometry: true
    });
    await savePDF(pdfEditOperations.ORGANIZE_PAGE_ROTATED);
    stores.organizeStore.setIsOrganizeOpInProgress(false);
    triggerAfterOrganizePages("rotatePages", params);
    if (getBookmarksStatus(stores) !== bookmarksStatuses.NOT_PRESENT) stores.bookmarkStore.getBookmarksFromDocAPI(true);
  } catch (error) {
    stores.organizeStore.setIsOrganizeOpInProgress(false);
    logOrganizeErrorData({
      featureType: FEATURE_TYPE,
      opType: OP_TYPE.ROTATE_PAGES_CW,
      errorInfo: { pageIndices, error }
    });
    console.error("Error in handleCWRotateClick", error);
  }
};

export const showDeletePageConfirmationDialog = (onConfirmHandler, onCancelHandler, opts = {}) => {
  dialogApi.show(dialogTypes.DELETE_PAGE_CONFIRMATION_DIALOG, {
    opts,
    onConfirm: async () => {
      dialogApi.hide();
      await onConfirmHandler();
    },
    onCancel: async () => {
      dialogApi.hide();
      await onCancelHandler();
    }
  });
};

const getFileDataFromIntegrationProvider = async () => {
  const integrationFactory = await providers.integrationProvider();
  const fileData = await integrationFactory.getInsertFileBuffer();
  return fileData;
};

const _getOrganizeFailureType = stores => {
  let organizeFailureType;
  if (isFilePasswordProtected(stores)) organizeFailureType = failureType.PASSWORD_PROTECTED;
  else if (isDocumentEncrypted(stores)) organizeFailureType = failureType.ENCRYPTED_DOCUMENT;
  else if (!isModifyingContentAllowed() && isReadOnlyModeEnabled(stores))
    organizeFailureType = failureType.READ_ONLY_DOCUMENT;
  else if (getHasDigSigFromMetadata(stores)) organizeFailureType = failureType.DIGITALLY_SIGNED_DOCUMENT;
  else if (hasSecurityBitSet()) organizeFailureType = failureType.POLICY_PROTECTED_DOCUMENT;
  else if (getIsPortfolio(stores)) organizeFailureType = failureType.PORT_FOLIO_FILE;
  // doubt if we need to stop for this
  else organizeFailureType = failureType.POLICY_PROTECTED_DOCUMENT;
  return organizeFailureType;
};

const _getIsDocumentUnOrganizable = stores =>
  isFilePasswordProtected(stores) ||
  isDocumentEncrypted(stores) ||
  (!isModifyingContentAllowed() && isReadOnlyModeEnabled(stores)) ||
  hasSecurityBitSet() ||
  getIsPortfolio(stores) ||
  getHasDigSigFromMetadata(stores);

const isFileUnsupported = (stores, opType) => {
  if (_getIsDocumentUnOrganizable(stores)) {
    const failureType = _getOrganizeFailureType(stores);
    logUnsupportedFileDialogShown({ featureType: FEATURE_TYPE, failureType, opType });
    return "UnsupportedFile";
  } else if (getFileShareType(stores) === "track" || getFileShareType(stores) === "review") {
    return "SharedFile";
  }
  return null;
};

const handleErrorHandling = (stores, opType) =>
  new Promise((resolve, reject) => {
    const fileType = isFileUnsupported(stores, opType);

    if (!fileType) {
      return resolve();
    }

    if (fileType === "UnsupportedFile") {
      dialogApi.show(dialogTypes.ORGANIZE_ERROR_HANDLING_DIALOG, {
        onConfirm: () => {
          dialogApi.hide();
          triggerOpenDocumentInDesktopEvent();
          reject();
        },
        onCancel: () => {
          dialogApi.hide();
          reject();
        }
      });
    } else if (fileType === "SharedFile") {
      logSharedFileDialogShown({ featureType: FEATURE_TYPE, opType });
      showFileCopyDialog();
      reject();
    } else {
      resolve();
    }
  });

const getIsInEditMode = async () => {
  try {
    const toolsBoardProvider = await providers["tools-board-provider"]();
    const currentConfig = toolsBoardProvider.getConfig();
    if (currentConfig && currentConfig.id === "group-edit") {
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error in getIsInEditMode", error);
    return false;
  }
};

export const handleDeletePages = async (stores, pageIndices) => {
  await handleErrorHandling(stores, "DeletePages");
  const onConfirmHandler = async () => {
    try {
      stores.organizeStore.setIsOrganizeOpInProgress(true);
      const totalPagesBeforeDelete = getTotalPages(stores);
      await logOrganizeOpsData(FEATURE_TYPE, OP_TYPE.DELETE_PAGES, {});
      await triggerBeforeOrganizePages({ opType: "deletePages", pageIndices });
      await docApi.organizeWillBegin({ intent: "delete-pages", fileUrl: getAssetURI(stores) });
      await docApi.deletePages({ pageIndices });
      CommandManager.clearStack();
      clearTileCanvas(stores);
      stores.documentStore.deletePages(pageIndices);
      if (pageIndices.length > 1) {
        pageIndices = pageIndices.reverse();
      }
      const params = { pageIndices };
      const dataSource = getThumbnailDataSource(stores);
      dataSource && dataSource.deleteItems(pageIndices);
      const metadataResponse = await docApi.getMetaDataFromAJS({
        forceCacheless: true
      });
      const totalPagesAfterDelete = metadataResponse.data.numPages;
      if (pageIndices.length !== totalPagesBeforeDelete - totalPagesAfterDelete) {
        throw new Error("Delete operation failed, failureType: SilentFailure");
      }
      trackDeleteOperationTriggered(pageIndices.length);
      await savePDF(pdfEditOperations.ORGANIZE_PAGE_DELETED);
      stores.organizeStore.setIsOrganizeOpInProgress(false);
      triggerAfterOrganizePages("deletePages", params);
      if (metadataResponse?.data?.hasBookmarks === true) {
        stores.bookmarkStore.getBookmarksFromDocAPI(true);
      }
    } catch (error) {
      stores.organizeStore.setIsOrganizeOpInProgress(false);
      logOrganizeErrorData({
        featureType: FEATURE_TYPE,
        opType: OP_TYPE.DELETE_PAGES,
        errorInfo: { pageIndices, error }
      });
      return error;
    }
  };

  const onCancelHandler = async () => {};
  await closeConflictingToolsBoardPanels();
  if (pageIndices.length > 1)
    await showDeletePageConfirmationDialog(onConfirmHandler, onCancelHandler, { multipage: true });
  else await showDeletePageConfirmationDialog(onConfirmHandler, onCancelHandler, { multipage: false });
};

export const handleInsertClick = async (stores, insertBeforePageIndex) => {
  await handleErrorHandling(stores, "InsertPages");
  let pagesAdded = 0;
  try {
    await closeConflictingToolsBoardPanels();
    trackOrganizeInsertTriggered();
    stores.organizeStore.setInsertPdfProgressLoaderIndex(insertBeforePageIndex);
    stores.organizeStore.setIsOrganizeOpInProgress(true);
    const filedata = await getFileDataFromIntegrationProvider();
    await logOrganizeOpsData(FEATURE_TYPE, OP_TYPE.INSERT_PAGES, {});
    if (!filedata?.arrayBuffer) throw new Error("Invalid Buffer");
    const arrayBuffer = filedata.arrayBuffer;
    const params = {
      afterPageIndex: insertBeforePageIndex - 1,
      pdfBuffer: new Uint8Array(arrayBuffer)
    };
    const isDocumentInsertableFlag = await isDocumentInsertable(arrayBuffer);
    if (!isDocumentInsertableFlag) {
      const messageId = "insert.pdf.unsupportedFile.message";
      const intl = getIntl(stores);
      stores.notificationViewStore.addNotification(intl.formatMessage({ id: messageId }), {
        type: "toast",
        timeout: 5000,
        variant: "error"
      });
      throw new Error("Unsupported file");
    }
    await triggerBeforeOrganizePages({ opType: "insertPages", ...params });
    const totalPagesBeforeInsert = getTotalPages(stores);
    let maxTotalPagesIndex = getMaxTotalPageIndex(stores);
    await docApi.organizeWillBegin({ intent: "insert-pdf", fileUrl: getAssetURI(stores) });
    await docApi.insertPages({ afterPageIndex: insertBeforePageIndex - 1, pdfBuffer: new Uint8Array(arrayBuffer) });
    CommandManager.clearStack();
    const metadataResponse = await docApi.getMetaDataFromAJS({
      forceCacheless: true
    });
    const totalPagesAfterInsert = metadataResponse.data.numPages;
    pagesAdded = totalPagesAfterInsert - totalPagesBeforeInsert;
    logOrganizeInsertData({
      featureType: FEATURE_TYPE,
      opType: OP_TYPE.INSERT_PAGES,
      mimeType: filedata?.mimeType,
      location: filedata?.fileLocation,
      downloadTime: filedata?.downloadTime,
      pagesAdded
    });
    clearTileCanvas(stores);
    const pageGeometries = metadataResponse.data.pages;
    if (pagesAdded > 0) {
      let pages = [];
      for (let i = 0; i < pagesAdded; i++) {
        const newPageId = maxTotalPagesIndex + i + 1;
        pages.push({
          ...getOriginalPageDimensions(pageGeometries, totalPagesAfterInsert, insertBeforePageIndex + i),
          index: insertBeforePageIndex + i,
          initialStatus: loadStatus.NOT_LOADED,
          id: newPageId
        });
      }
      maxTotalPagesIndex += pagesAdded;
      stores.documentStore.setMaxTotalPagesIndex(maxTotalPagesIndex + 1);
      stores.documentStore.insertPages(insertBeforePageIndex, pages);
      await stores.documentStore.reloadPagesAndGeometries(
        insertBeforePageIndex + 1,
        insertBeforePageIndex + pagesAdded,
        {
          refreshGeometry: true
        }
      );
      stores.pagedViewState.goToPage(insertBeforePageIndex + 1);
      const dataSource = getThumbnailDataSource(stores);
      dataSource && dataSource.insertItem(insertBeforePageIndex, pages);
      params.numPagesInserted = pagesAdded;
      await savePDF(pdfEditOperations.ORGANIZE_PAGE_INSERTED);
      triggerAfterOrganizePages("insertPages", params);
    } else {
      throw new Error("Insert operation failed, failureType: SilentFailure");
    }

    // upload is ended
    stores.organizeStore.setInsertPdfProgressLoaderIndex(null);
    stores.organizeStore.setIsOrganizeOpInProgress(false);
    if (metadataResponse?.data?.hasBookmarks === true || getBookmarksStatus(stores) !== bookmarksStatuses.NOT_PRESENT) {
      stores.bookmarkStore.getBookmarksFromDocAPI(true);
    }
  } catch (error) {
    logOrganizeErrorData({
      featureType: FEATURE_TYPE,
      opType: OP_TYPE.INSERT_PAGES,
      errorInfo: { pagesAdded, insertBeforePageIndex, error }
    });
    stores.organizeStore.setInsertPdfProgressLoaderIndex(null);
    stores.organizeStore.setIsOrganizeOpInProgress(false);
    console.error("Error in insertPages", error);
  }
};

export const handleReorderPages = async (stores, pageIndices, afterPageIndex) => {
  try {
    await handleErrorHandling(stores, "ReorderPages");
    trackReorderOperationTriggered(pageIndices.length);
    await logOrganizeOpsData(FEATURE_TYPE, OP_TYPE.REORDER_PAGES, {});
    await closeConflictingToolsBoardPanels();
    const params = { pageIndices, afterPageIndex };
    stores.organizeStore.setIsOrganizeOpInProgress(true);
    await triggerBeforeOrganizePages({ opType: "reorderPages", ...params });
    await docApi.organizeWillBegin({ intent: "reorder-pages", fileUrl: getAssetURI(stores) });
    await docApi.reorderPages({ pageIndices, afterPageIndex });
    CommandManager.clearStack();
    clearTileCanvas(stores);
    stores.documentStore.reorderPages(pageIndices, afterPageIndex);
    await savePDF(pdfEditOperations.ORGANIZE_PAGE_REORDERED);
    triggerAfterOrganizePages("reorderPages", params);
    stores.organizeStore.setIsOrganizeOpInProgress(false);
    if (getBookmarksStatus(stores) !== bookmarksStatuses.NOT_PRESENT) stores.bookmarkStore.getBookmarksFromDocAPI(true);
  } catch (error) {
    stores.organizeStore.setIsOrganizeOpInProgress(false);
    logOrganizeErrorData({
      featureType: FEATURE_TYPE,
      opType: OP_TYPE.REORDER_PAGES,
      errorInfo: { error, pageIndices, afterPageIndex }
    });
    console.error("Error in handleReorderPages", error);
  }
};
